import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Alert, AlertCircle, Button, Modal } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError } from "../../../shared/toastHelper";

const DeleteSegmentModal = ({ segment, team, onCancel, onConfirm }) => {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const lastSegmentRef = useRef(segment);
  if (segment) lastSegmentRef.current = segment;
  const shownSegment = lastSegmentRef.current;

  useEffect(() => {
    if (!segment) return undefined;
    let active = true;
    setAutomations([]);
    setError(false);
    setLoading(true);
    client
      .get(`/teams/${team.id}/segments/${segment.id}/references.json`)
      .then((response) => {
        if (!active) return;
        setAutomations(response.data.automations || []);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setAutomations([]);
        setError(true);
        setLoading(false);
        toastError("Couldn't check whether automations use this segment");
      });
    return () => {
      active = false;
    };
  }, [segment, team.id]);

  return (
    <Modal
      open={Boolean(segment)}
      onClose={onCancel}
      type="error"
      icon={AlertCircle}
      title="Delete segment?"
      description={shownSegment ? `“${shownSegment.name}” will be permanently deleted. This can’t be undone.` : null}
      footer={(
        <>
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button variant="destructive" disabled={loading || !segment} onClick={() => onConfirm(segment)}>Delete</Button>
        </>
      )}
    >
      {(error || automations.length > 0) && (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {error && <Alert color="error" role="alert" title="Couldn't check whether automations use this segment" />}
          {automations.length > 0 && (
            <Alert
              color="warning"
              title={`Used by ${automations.length} active automation${automations.length === 1 ? "" : "s"}`}
              description="Deleting this segment will change who those automations target — a “Fan is not in” condition will match everyone, and a segment recipient will send to no one."
            />
          )}
          {automations.length > 0 && (
            <ul aria-label="Automations using this segment" style={{ margin: 0, paddingLeft: "18px", fontSize: "14px", lineHeight: "20px", color: "var(--sl-text-secondary)" }}>
              {automations.map((automation) => (
                <li key={automation.id}>{automation.name}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
};

DeleteSegmentModal.propTypes = {
  segment: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  team: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  }).isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default DeleteSegmentModal;
